import React from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import SmallCallItem from 'flow/components/communication-canvas/SmallCallItem';
import { getOwnCountryCode } from 'flow/selectors/selectors';
import {
    getLocalSessionFromCallId,
    getOwnActiveCallByCallId,
} from 'flow/selectors/call-selectors';
import { makeGetContactDataForNumber } from 'flow/selectors/contact-selectors';
import { setViewToSideBar } from 'flow/actions/meeting-actions';
import { answerCall, hangupSession } from 'flow/actions/phone-actions';
import { convertNumberToE164 } from 'flow/utils/call-utils';

interface MeetingCallNotificationProps {
    callId: string;
}

const MeetingCallNotification: React.FC<MeetingCallNotificationProps> = ({
    callId,
}) => {
    const dispatch = useAppDispatch();

    const ownCountryCode = useAppSelector(getOwnCountryCode);
    const initialSession = useAppSelector((state) =>
        getLocalSessionFromCallId(state, callId),
    );
    const activeTopCall = useAppSelector((state) =>
        getOwnActiveCallByCallId(state, callId),
    );

    const activeCallRemoteNumber =
        activeTopCall &&
        (activeTopCall.get('direction') === 'remote_start'
            ? activeTopCall.get('destination')
            : activeTopCall.get('source'));
    const remoteNumberFromTop = !activeTopCall ? null : activeCallRemoteNumber;
    const remoteIdentity = initialSession?.remote_identity?.uri?.user;
    const remoteNumber = convertNumberToE164(
        remoteNumberFromTop || remoteIdentity,
        ownCountryCode,
    );

    const getContact = React.useMemo(() => makeGetContactDataForNumber(), []);
    const contact = useAppSelector((state) => {
        const contactSelector = getContact(state);
        return contactSelector ? contactSelector(remoteNumber) : null;
    });

    const handleAnswerCall = (call: any) => dispatch(answerCall(call));
    const handleHangUpCall = (call: any) => dispatch(hangupSession(call));
    const setSidebarView = () => dispatch(setViewToSideBar());

    const hangupIncomingCall = () => {
        handleHangUpCall(initialSession);
    };

    const answerNewCall = () => {
        setSidebarView();
        handleAnswerCall(initialSession);
    };

    return (
        <div className="w-[500px]" data-test-id={'call-notification'}>
            {callId && remoteNumber ? (
                <SmallCallItem
                    callId={callId}
                    contact={contact}
                    ownCountryCode={ownCountryCode}
                    remoteNumber={remoteNumber}
                    direction={'incoming'}
                    onAnswer={answerNewCall}
                    onDecline={hangupIncomingCall}
                    isEnded={false}
                    isOnHold={false}
                    isAccepted={false}
                />
            ) : null}
        </div>
    );
};

export default MeetingCallNotification;
